import React, { useContext } from 'react';
import _merge from 'lodash/merge';
import { useSpring } from 'react-spring';
import { SPEED_NORMAL } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import ArrowTriangleDownIcon from '@nokia-csf-uxr/ccfk-assets/ArrowTriangleDownIcon';
import { getContext, isAnimationDisabled } from 'common';
import IconButton, { IconButtonProps } from 'components/IconButton';
import { getEasing, getDuration } from 'common/parse-tokens';
import { StepContext } from './Step';
import Styled from './styles';

const SPRING_CONFIG = {
  duration: getDuration(SPEED_NORMAL),
  easing: getEasing(TRANSITION_ACCELERATE)
};

export interface StepDropDownButtonProps {
  /** An `Icon` component. */
  children?: React.ReactNode;
  /** Override the `IconButton` properties. See `IconButton` component for details. */
  iconButtonProps?: Partial<IconButtonProps>;
  /* If true, the dropdown button is disabled. */
  disabled?: boolean;
  /** Control the animation of the icon button. Leverages 'react-spring' useSpring. See https?://react-spring.io/ */
  animationProps?: any;
  /** If true, disable all motion animation for the icon button. This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  isOpen?: boolean;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  style?: any,
}

/** A component to use as the dropdown indicator on a parent Step. Any other props will be provided to the Wrapper component. */
const StepDropDownButton = React.forwardRef<HTMLElement, StepDropDownButtonProps>((props: StepDropDownButtonProps, ref) => getContext(({ theme, disableAnimation }) => {
  const {
    children,
    iconButtonProps,
    animationProps,
    disableAnimation: propsdisableAnimation,
    disabled,
    theme: propTheme,
    isOpen,
    ...otherProps
  } = props;
  const { style, ...otherIconButtonProps } = iconButtonProps;
  const stepContext = useContext(StepContext);
  const resolvedTheme = _merge({}, theme.STEP_FORM, propTheme.STEP_FORM);
  const resolvedIsOpen = isOpen || stepContext.isOpen;
  const expandButtonStyle = useSpring(animationProps || {
    transform: resolvedIsOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
    config: SPRING_CONFIG,
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation)
  });
  const { style: userStyle, ...allOtherProps } = otherProps;
  const resolvedStyle = _merge({}, expandButtonStyle, userStyle);
  const handleKeyDown = (event) => {
    const { onKeyDown } = iconButtonProps;
    // this button allows no onKeyDown events
    event.stopPropagation();
    event.preventDefault();
    // but if user wants callback, give it to them
    onKeyDown && onKeyDown(event);
  };
  const handleOnClick = (event) => {
    const { onClick } = iconButtonProps;
    event.preventDefault();
    onClick && onClick(event);
  };

  return (
    <Styled.StepDropDownButton
      style={resolvedStyle}
      $theme={resolvedTheme}
      $isOpen={resolvedIsOpen}
      {...allOtherProps}
    >
      <IconButton
        ref={ref}
        tabIndex={-1}
        style={{ ...style }}
        disabledRipple
        disabled={disabled}
        aria-hidden
        {...otherIconButtonProps}
        onKeyDown={handleKeyDown}
        onClick={handleOnClick}
      >
        {children}
      </IconButton>
    </Styled.StepDropDownButton>
  );
}));

StepDropDownButton.defaultProps = {
  children: <ArrowTriangleDownIcon size={24} aria-label={null} />,
  iconButtonProps: {},
  animationProps: undefined,
  disableAnimation: undefined,
  disabled: false,
  theme: {},
  onClick: undefined,
  onKeyDown: undefined,
  isOpen: undefined,
  style: {},
};

StepDropDownButton.displayName = 'StepDropDownButton';

export default StepDropDownButton;